import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ShieldAlert, Trash2, Loader2, UserCheck } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { Drawer, fdate, btnGhost } from '@/components/dashboard/kit';
import { useConfirm } from './ConfirmDialog';
import { plural } from './people';

/*
  Cuentas creadas por bots (23/10/2026, backend/routes/admin_bots.py).
  Aviso encima de la lista de Personas cuando hay cuentas que cumplen TODOS los criterios: sin membresía,
  email sin verificar, nombre aleatorio y nada vinculado (red, compras, documentos, CDM, inscripciones, leads).
  Se revisan en un panel lateral y se borran con confirmación fuerte (escribir ELIMINAR).
  Si una es de una persona real, «No es un bot» la saca de la lista para siempre (02/11/2026).
  Desde el 02/11/2026 incluye también contactos de la web y tests de idoneidad sin cuenta con nombre sin sentido.
*/
const KIND = { account: 'Cuenta', lead: 'Contacto web', test: 'Test de idoneidad' };
export default function BotSuspects({ onChanged }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(null);
  const [dialog, ask] = useConfirm();

  const load = useCallback(() => {
    dashboardApi.adminBotSuspects().then(setData).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data?.total) return null;
  const n = data.total;

  const notBot = async (u) => {
    setSaving(u.key || u.id);
    try {
      await dashboardApi.adminNotBot(u.key || u.id);
      toast.success(`${u.name} ya no aparece como bot`);
      setData((d) => ({ ...d, items: d.items.filter((x) => (x.key || x.id) !== (u.key || u.id)), total: d.total - 1 }));
    } catch (e) {
      toast.error(e.message || 'No se ha podido guardar');
    } finally {
      setSaving(null);
    }
  };

  const remove = async () => {
    const ok = await ask({
      title: `Eliminar ${plural(n, 'registro', 'registros')} de bots`,
      description: 'Se borran de la plataforma. No se puede deshacer.',
      details: [
        'Cuentas: sin membresía, con el email sin verificar, nombre aleatorio y sin red, compras, documentos, CDM, inscripciones ni leads.',
        'Contactos y tests: nombre aleatorio, sin cuenta y sin que nadie del equipo los haya trabajado.',
        'Antes de borrar cada una se vuelve a comprobar: si algo ha cambiado, se deja.',
      ],
      confirmLabel: 'Eliminar', requireText: 'ELIMINAR',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await dashboardApi.adminDeleteBotSuspects(data.items.map((u) => u.key || u.id));
      toast.success(`${plural(r.deleted, 'registro eliminado', 'registros eliminados')}${r.skipped ? ` · ${r.skipped} se han dejado` : ''}`);
      setOpen(false);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.message || 'No se han podido eliminar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-400/25 bg-amber-400/[0.06] px-4 py-3" data-testid="bot-suspects-banner">
        <p className="flex items-center gap-2 text-sm text-amber-100">
          <ShieldAlert size={16} className="shrink-0 text-amber-300" />
          {plural(n, 'registro parece creado', 'registros parecen creados')} por bots (nombre aleatorio y sin nada vinculado).
          {data.counts && ` ${[['account', 'cuentas'], ['lead', 'contactos'], ['test', 'tests']].filter(([k]) => data.counts[k]).map(([k, l]) => `${data.counts[k]} ${l}`).join(' · ')}.`}
        </p>
        <button type="button" onClick={() => setOpen(true)} className={`${btnGhost} h-9`} data-testid="bot-suspects-review">Revisar</button>
      </div>

      <Drawer open={open} onClose={() => setOpen(false)} title="Registros creados por bots" subtitle={plural(n, 'registro', 'registros')}
        footer={(<>
          <button type="button" onClick={() => setOpen(false)} className={`${btnGhost} h-10`}>Cerrar</button>
          <button type="button" onClick={remove} disabled={busy}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-red-500/90 px-4 text-sm font-semibold text-white disabled:opacity-50"
            data-testid="bot-suspects-delete">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Eliminar {n}
          </button>
        </>)}>
        <ul className="divide-y divide-white/[0.06]" data-testid="bot-suspects-list">
          {data.items.map((u) => (
            <li key={u.key || u.id} className="flex items-start justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <p className="text-sm text-white break-all">{u.name} <span className="ml-1 rounded border border-white/10 px-1.5 py-0.5 text-[11px] text-gray-400">{KIND[u.kind] || 'Cuenta'}</span></p>
                <p className="text-xs text-gray-400 break-all">{u.email} · {u.phone || 'sin teléfono'} · {fdate(u.created_at)}</p>
              </div>
              <button type="button" onClick={() => notBot(u)} disabled={saving === (u.key || u.id)}
                className={`${btnGhost} h-8 shrink-0 text-xs`} data-testid="bot-suspects-not-bot">
                {saving === (u.key || u.id) ? <Loader2 size={13} className="animate-spin" /> : <UserCheck size={13} />} No es un bot
              </button>
            </li>
          ))}
        </ul>
        {/* Dentro del panel lateral: el diálogo tiene que quedar por encima de él */}
        {dialog}
      </Drawer>
    </>
  );
}
